import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useInstatDesign } from '../theme/instatDesign';

/**
 * Apple-style radar chart, custom SVG (no lib).
 *
 * Props:
 *   axes: [{ key, label, value (0..100), icon? }]
 *   size: pixel size
 *   maxValue: defaults to 100
 *   animate: bool — animate ring polygon on mount
 *   onAxisClick: (axis) => void
 */
export default function SkillRadar({
  axes = [],
  size = 320,
  maxValue = 100,
  animate = true,
  onAxisClick,
  sx = {},
}) {
  const d = useInstatDesign();
  const [t, setT] = useState(animate ? 0 : 1); // 0..1 animation progress
  const rafRef = useRef(null);

  useEffect(() => {
    if (!animate) {
      setT(1);
      return;
    }
    let start = null;
    const duration = 700;
    const tick = (ts) => {
      if (start == null) start = ts;
      const elapsed = ts - start;
      const raw = Math.min(1, elapsed / duration);
      // easeOutBack (Apple spring-ish)
      const c1 = 1.3;
      const c3 = c1 + 1;
      const eased = 1 + c3 * Math.pow(raw - 1, 3) + c1 * Math.pow(raw - 1, 2);
      setT(eased);
      if (raw < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [animate, axes]);

  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.38;
  const n = axes.length;

  if (n < 3) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: size }}>
        <Typography variant="caption" color="text.secondary">
          Not enough data for radar
        </Typography>
      </Box>
    );
  }

  const angleFor = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / n;

  // Grid rings — 4 concentric polygons (25/50/75/100%)
  const rings = [0.25, 0.5, 0.75, 1];

  const axisPt = (i, ratio = 1) => {
    const a = angleFor(i);
    return {
      x: cx + Math.cos(a) * outerR * ratio,
      y: cy + Math.sin(a) * outerR * ratio,
    };
  };

  const polygonPts = (ratio) =>
    axes.map((_, i) => {
      const p = axisPt(i, ratio);
      return `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
    }).join(' ');

  const valuePts = axes.map((a, i) => {
    const v = Math.max(0, Math.min(maxValue, Number(a.value) || 0)) / maxValue;
    return axisPt(i, v * t);
  });
  const valuePath = valuePts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');

  const gradId = `radar-grad-${Math.random().toString(36).slice(2, 8)}`;

  // Label placement
  const labelFor = (i) => {
    const a = angleFor(i);
    const labelR = outerR + size * 0.09;
    const x = cx + Math.cos(a) * labelR;
    const y = cy + Math.sin(a) * labelR;
    let anchor = 'middle';
    if (Math.cos(a) > 0.25) anchor = 'start';
    else if (Math.cos(a) < -0.25) anchor = 'end';
    return { x, y, anchor };
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', ...sx }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }}>
        <defs>
          <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor={d.accent} stopOpacity="0.28" />
            <stop offset="100%" stopColor={d.accent} stopOpacity="0.08" />
          </radialGradient>
        </defs>

        {/* Grid rings */}
        {rings.map((r, i) => (
          <polygon
            key={i}
            points={polygonPts(r)}
            fill={i === rings.length - 1 ? 'transparent' : d.colors.canvasAlt}
            stroke={d.colors.divider}
            strokeWidth={i === rings.length - 1 ? 1.5 : 1}
          />
        ))}

        {/* Axis spokes */}
        {axes.map((a, i) => {
          const p = axisPt(i, 1);
          return (
            <line
              key={`spoke-${a.key}`}
              x1={cx}
              y1={cy}
              x2={p.x}
              y2={p.y}
              stroke={d.colors.divider}
              strokeWidth="1"
            />
          );
        })}

        {/* Value polygon */}
        <polygon
          points={valuePath}
          fill={`url(#${gradId})`}
          stroke={d.accent}
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* Value dots */}
        {valuePts.map((p, i) => (
          <circle
            key={`dot-${axes[i].key}`}
            cx={p.x}
            cy={p.y}
            r="4"
            fill={d.colors.surface}
            stroke={d.accent}
            strokeWidth="2"
            style={{ cursor: onAxisClick ? 'pointer' : 'default' }}
            onClick={() => onAxisClick && onAxisClick(axes[i])}
          />
        ))}

        {/* Axis labels + values */}
        {axes.map((a, i) => {
          const lbl = labelFor(i);
          const raw = Math.round(a.value || 0);
          return (
            <g key={`lbl-${a.key}`} style={{ cursor: onAxisClick ? 'pointer' : 'default' }} onClick={() => onAxisClick && onAxisClick(a)}>
              <text
                x={lbl.x}
                y={lbl.y - 4}
                textAnchor={lbl.anchor}
                fill={d.colors.textSecondary}
                fontSize="10.5"
                fontWeight="600"
                letterSpacing="0.04em"
                fontFamily={d.typo.fontFamily}
                style={{ textTransform: 'uppercase' }}
              >
                {a.label}
              </text>
              <text
                x={lbl.x}
                y={lbl.y + 10}
                textAnchor={lbl.anchor}
                fill={d.colors.text}
                fontSize="15"
                fontWeight="700"
                fontFamily={d.typo.fontFamily}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {raw}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
}
